import { Bar, BarChart, Cell, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import type { VillageIncidentChartCardData } from "../model/villageIncidentCharts";
import { useVillageChartConfigState } from "../hooks/useVillageChartConfigState";
import { truncateChartAxisLabel } from "../model/chartLabelUtils";
import { ChartModeToggle } from "./ChartModeToggle";
import { ChartDimensionSelect } from "./ChartDimensionSelect";

type VillageIncidentChartCardProps = {
  card: VillageIncidentChartCardData;
};

const CHART_COLORS = ["#0a7a36", "#1f6a9d", "#c67100", "#7d3069", "#8f9aa3", "#2f5d50", "#c03b2d"];
const TOOLTIP_CONTENT_STYLE = {
  fontSize: "11px",
  borderRadius: "6px",
  borderColor: "rgba(10, 122, 54, 0.28)",
};
const TOOLTIP_LABEL_STYLE = {
  fontSize: "11px",
};
const TOOLTIP_ITEM_STYLE = {
  fontSize: "11px",
  color: "var(--green)",
  fontWeight: 700,
};

export function VillageIncidentChartCard({ card }: VillageIncidentChartCardProps) {
  const { chartMode, dimensionKey, data, setDimensionKey, toggleMode } = useVillageChartConfigState(card);
  const chartCanvasId = `${card.id}-chart-canvas`;

  return (
    <article className="village-incident-chart-card">
      <header className="village-incident-chart-card__header">
        <div className="village-incident-chart-card__title-row">
          <h4>{card.title}</h4>
          <img src={card.iconPath} alt="" loading="lazy" />
        </div>
        <ChartModeToggle
          mode={chartMode}
          chartTitle={card.title}
          controlsId={chartCanvasId}
          onToggle={toggleMode}
        />
      </header>

      <div id={chartCanvasId} className="village-incident-chart-card__canvas" role="img" aria-label={`${card.title} chart`}>
        {data.length > 0 ? (
          chartMode === "bar" ? (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={data} margin={{ top: 6, right: 70, left: 30, bottom: 36 }}>
                <XAxis dataKey="label" angle={-22} textAnchor="end" interval={0} height={72} tick={{ fontSize: 14 }} tickMargin={8} tickFormatter={truncateChartAxisLabel} />
                <YAxis allowDecimals={false} tick={{ fontSize: 14 }} width={36} />
                <Tooltip contentStyle={TOOLTIP_CONTENT_STYLE} labelStyle={TOOLTIP_LABEL_STYLE} itemStyle={TOOLTIP_ITEM_STYLE} />
                <Bar dataKey="count" fill="#0a7a36" radius={[4, 4, 0, 0]} barSize={56} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Tooltip contentStyle={TOOLTIP_CONTENT_STYLE} labelStyle={TOOLTIP_LABEL_STYLE} itemStyle={TOOLTIP_ITEM_STYLE} />
                <Pie data={data} dataKey="count" nameKey="label" cx="50%" cy="50%" outerRadius={90}>
                  {data.map((entry, index) => (
                    <Cell key={`${entry.label}-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
          )
        ) : (
          <p className="small-paragraph">No records are available for this dimension.</p>
        )}
      </div>

      <ChartDimensionSelect
        cardId={card.id}
        chartTitle={card.title}
        value={dimensionKey}
        dimensions={card.dimensions}
        onChange={setDimensionKey}
      />
    </article>
  );
}
